import { supabase } from '@/integrations/supabase/client';
import type { ChecklistFieldMapping } from './types';

type FieldLookupRow = {
  id: string;
  template_item_id: string;
  field_mapping_id: string;
  display_order: number;
  field_mapping?: {
    id: string;
    datapoint?: {
      id: string;
      display_name: string;
      bucket_name: string;
      measurement_name: string;
      field_name: string;
      unit?: { symbol: string | null } | null;
    } | null;
  } | null;
};

function mapFieldMappingRow(row: FieldLookupRow): ChecklistFieldMapping {
  return {
    id: row.id,
    checklist_item_id: row.template_item_id,
    field_mapping_id: row.field_mapping_id,
    display_order: row.display_order,
    field_mapping: row.field_mapping
      ? {
          id: row.field_mapping.id,
          datapoint: row.field_mapping.datapoint
            ? {
                id: row.field_mapping.datapoint.id,
                display_name: row.field_mapping.datapoint.display_name,
                bucket_name: row.field_mapping.datapoint.bucket_name,
                measurement_name: row.field_mapping.datapoint.measurement_name,
                field_name: row.field_mapping.datapoint.field_name,
                unit: row.field_mapping.datapoint.unit ?? null,
              }
            : null,
        }
      : null,
  };
}

const FIELD_MAPPING_SELECT = `
  id,
  template_item_id,
  field_mapping_id,
  display_order,
  field_mapping:field_mappings(
    id,
    datapoint:datapoints(
      id,
      display_name,
      bucket_name,
      measurement_name,
      field_name,
      unit:units(symbol)
    )
  )
`;

export async function fetchChecklistItemFieldMappings(
  tenantId: string,
  checklistItemId: string,
): Promise<ChecklistFieldMapping[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_checklist_item_field_mappings')
    .select(FIELD_MAPPING_SELECT)
    .eq('tenant_id', tenantId)
    .eq('template_item_id', checklistItemId)
    .order('display_order');

  if (error) throw error;
  return ((data ?? []) as FieldLookupRow[]).map(mapFieldMappingRow);
}

export async function fetchChecklistFieldMappingsForItems(
  tenantId: string,
  checklistItemIds: string[],
): Promise<Record<string, ChecklistFieldMapping[]>> {
  if (checklistItemIds.length === 0) return {};

  const { data, error } = await supabase
    .schema('core')
    .from('mod_checklist_item_field_mappings')
    .select(FIELD_MAPPING_SELECT)
    .eq('tenant_id', tenantId)
    .in('template_item_id', checklistItemIds)
    .order('display_order');

  if (error) throw error;

  const grouped: Record<string, ChecklistFieldMapping[]> = {};
  for (const row of (data ?? []) as FieldLookupRow[]) {
    const mapped = mapFieldMappingRow(row);
    (grouped[mapped.checklist_item_id] ??= []).push(mapped);
  }
  return grouped;
}

export async function replaceChecklistItemFieldMappings(
  tenantId: string,
  checklistItemId: string,
  fieldMappingIds: string[],
): Promise<void> {
  const uniqueIds = [...new Set(fieldMappingIds)];

  const { error: deleteError } = await supabase
    .schema('core')
    .from('mod_checklist_item_field_mappings')
    .delete()
    .eq('tenant_id', tenantId)
    .eq('template_item_id', checklistItemId);

  if (deleteError) throw deleteError;

  if (uniqueIds.length === 0) return;

  const { error: insertError } = await supabase
    .schema('core')
    .from('mod_checklist_item_field_mappings')
    .insert(
      uniqueIds.map((fieldMappingId, index) => ({
        tenant_id: tenantId,
        template_item_id: checklistItemId,
        field_mapping_id: fieldMappingId,
        display_order: index,
      })),
    );

  if (insertError) throw insertError;
}
